import i18next from '@dw/i18next-esm';
import isEmpty from 'lodash-es/isEmpty';
import filter from 'lodash-es/filter';
import sortBy from 'lodash-es/sortBy';
import get from 'lodash-es/get';
import map from 'lodash-es/map';
import orderBy from 'lodash-es/orderBy';
import { getFormatDate } from '../utils.js';
import { getAttachmentLink } from '../redux/attachments/utils.js';

/**
 * Builds the Markdown representation of a card.
 *
 * Opens with a `# Task 12: [title](url)` heading (`# Task: [title](url)` when the
 * board's auto-numbering is off or the card has no number), followed by `## Label`
 * sections: Metadata, Description, Sub-tasks, Attachments and Comments.
 *
 * @param {Object} card
 *  @property {String} card.title Card title.
 *  @property {String} card.url Card's full link.
 *  @property {Number} card.refNo Card's number on its board.
 *  @property {Boolean} card.showCardRefNo `true` when the card's own board has auto-numbering on.
 *  @property {String} card.descriptionMd Card description, already converted to Markdown.
 *  @property {String} card.status Card status code (e.g. `IN_PROGRESS`).
 *  @property {String} card.priority Card priority code (e.g. `HIGH_PRIORITY`).
 *  @property {Number} card.dueDate Due date timestamp.
 *  @property {Array} card.assigneeNames Resolved assignee user names.
 *  @property {Array} card.tagNames Resolved tag names.
 *  @property {String} card.columnType Type of the card's column (`DONE`, `TRASH`, ...).
 *  @property {Array} card.tasks Card's sub-task docs, each with a precomputed `titleMd` (title2/title converted to Markdown).
 *  @property {Object} card.userNamesById Map of user id → resolved user name.
 *  @property {Array} card.attachments Card's attachment docs.
 *  @property {Object} card.state Redux state, used to resolve attachment links.
 *  @property {Array} card.comments Visible comments, each `{ createdBy, createdAt, lastUpdatedAt, bodyMd }`.
 * @returns {String} Markdown source for the card.
 */
export function formatCard({ title, url, refNo, showCardRefNo, descriptionMd, status, priority, dueDate, assigneeNames, tagNames, columnType, tasks, userNamesById, attachments, state, comments } = {}) {
  const linkText = escapeLinkText(title);
  const sections = [`# Task${showCardRefNo && refNo ? ` ${refNo}` : ''}: ${url ? `[${linkText}](${url})` : linkText}`];

  const metadata = renderMetadataBlock({ status, priority, dueDate, assigneeNames, tagNames, columnType });
  if (metadata) {
    sections.push(metadata);
  }

  if (descriptionMd && descriptionMd.trim()) {
    sections.push(`## Description\n${descriptionMd.trim()}`);
  }

  const subTasks = renderSubTasksSection(tasks, userNamesById);
  if (subTasks) {
    sections.push(subTasks);
  }

  const attachmentsSection = renderAttachmentsSection(attachments, state);
  if (attachmentsSection) {
    sections.push(attachmentsSection);
  }

  const commentsSection = renderCommentsSection(comments, userNamesById);
  if (commentsSection) {
    sections.push(commentsSection);
  }

  return sections.join('\n\n');
}

/**
 * Escapes the characters that would break a Markdown link's `[text]`.
 */
export function escapeLinkText(text) {
  return (text || '').replace(/[\\[\]]/g, '\\$&');
}

/**
 * Renders the card's metadata as a `## Metadata` section of bulleted rows. Each
 * row is omitted when its field is unset / default / empty; the whole section is
 * omitted when every row would be omitted.
 */
function renderMetadataBlock({ status, priority, dueDate, assigneeNames, tagNames, columnType }) {
  const rows = [];

  // Effective status mirrors `kerika-board-card-details.js`: DONE / TRASH columns
  // override the explicit status.
  const effectiveStatus = columnType === 'DONE' ? 'DONE' : columnType === 'TRASH' ? 'TRASH' : status || 'NORMAL';
  if (effectiveStatus !== 'NORMAL') {
    rows.push(`- **Status:** ${i18next.t(`card.status.${effectiveStatus}`)}`);
  }

  if (priority && priority !== 'NORMAL') {
    rows.push(`- **Priority:** ${i18next.t(`card.priority.${priority}`)}`);
  }

  if (dueDate) {
    rows.push(`- **Due:** ${getFormatDate(dueDate, 'YYYY-MM-DD')}`);
  }

  if (!isEmpty(assigneeNames)) {
    rows.push(`- **Assigned to:** ${assigneeNames.join(', ')}`);
  }

  if (!isEmpty(tagNames)) {
    rows.push(`- **Tags:** ${tagNames.join(', ')}`);
  }

  if (isEmpty(rows)) {
    return '';
  }

  return `## Metadata\n${rows.join('\n')}`;
}

/**
 * Renders the card's sub-tasks as a `## Sub-tasks` GFM task list. Open tasks
 * are listed first (`- [ ]`), then closed tasks (`- [x]`), each block sorted by
 * `order` ascending. The section is omitted when the card has no sub-tasks.
 *
 * @param {Array} tasks Sub-task docs.
 * @param {Object} userNamesById Map of user id → resolved user name.
 */
function renderSubTasksSection(tasks, userNamesById = {}) {
  if (isEmpty(tasks)) {
    return '';
  }

  const openTasks = sortBy(filter(tasks, (task) => !task.closed), ['order']);
  const closedTasks = sortBy(filter(tasks, { closed: true }), ['order']);

  const lines = [
    ...map(openTasks, (task) => renderSubTaskLine(task, false, userNamesById)),
    ...map(closedTasks, (task) => renderSubTaskLine(task, true, userNamesById)),
  ];

  return `## Sub-tasks\n${lines.join('\n')}`;
}

/**
 * Renders a single sub-task as a GFM task list item with optional assignees and
 * due date. The title is the already-converted Markdown (`titleMd`); any line
 * breaks are collapsed to keep the task on a single list-item line.
 */
function renderSubTaskLine(task, closed, userNamesById) {
  const titleMd = (get(task, 'titleMd') || '').replace(/\s*\n+\s*/g, ' ').trim();
  let line = `- [${closed ? 'x' : ' '}] ${titleMd}`;

  const assigneeNames = map(get(task, 'assignments') || [], (userId) => userNamesById[userId]).filter(Boolean);
  if (!isEmpty(assigneeNames)) {
    line += ` (${assigneeNames.join(', ')})`;
  }

  const dueDate = get(task, 'dueDate');
  if (dueDate) {
    line += ` (due ${getFormatDate(dueDate, 'YYYY-MM-DD')})`;
  }

  return line;
}

/**
 * Renders the card's attachments as a `## Attachments` section of Markdown links,
 * sorted by `order` descending (UI parity). Entries without a usable URL are
 * skipped; the section is omitted when the card has no linkable attachments.
 *
 * @param {Array} attachments Attachment docs.
 * @param {Object} state Redux state, used to resolve attachment links.
 */
function renderAttachmentsSection(attachments, state) {
  if (isEmpty(attachments)) {
    return '';
  }

  const lines = [];
  orderBy(attachments, ['order'], ['desc']).forEach((model) => {
    const url = getAttachmentLink(state, model);
    if (!url) {
      return;
    }
    lines.push(`- [${get(model, 'title') || url}](${url})`);
  });

  if (isEmpty(lines)) {
    return '';
  }

  return `## Attachments\n${lines.join('\n')}`;
}

/**
 * Renders the card's comments as a `## Comments` section. Each comment is a
 * `### Author — date-time` heading followed by the comment body (already
 * converted to Markdown, with `@usr_xxx` mentions resolved to names). No rule
 * separates comments, so `---` only ever separates two cards. The section is
 * omitted when there are no visible comments.
 *
 * @param {Array} comments Visible comments, each `{ createdBy, createdAt, lastUpdatedAt, bodyMd }`.
 * @param {Object} userNamesById Map of user id → resolved user name.
 */
function renderCommentsSection(comments, userNamesById = {}) {
  if (isEmpty(comments)) {
    return '';
  }

  const blocks = map(comments, (comment) => {
    const author = userNamesById[comment.createdBy] || comment.createdBy || '';
    // Prefer the edited time (`lastUpdatedAt`) over the original `createdAt`.
    const header = `### ${author} — ${getFormatDate(comment.lastUpdatedAt || comment.createdAt, 'YYYY-MM-DD hh:mm A')}`;
    const body = (comment.bodyMd || '').trim();
    return body ? `${header}\n\n${body}` : header;
  });

  return `## Comments\n\n${blocks.join('\n\n')}`;
}
